import {
  AskStrikeUpButton,
  Badge,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  Input,
  RefreshButton,
  Select,
  Table,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdCheckCircle,
  MdError,
  MdList,
  MdSearch,
  MdVisibility,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ServerConfig, serverService } from "services/serverService";
import {
  PaginatedResult,
  ToolExecutionLog,
  toolExecutionLogsService,
  FAILURE_KINDS,
  FailureKind,
} from "services/toolExecutionLogsService";
import { getThemeColor } from "theme/colors";

const ToolExecutionLogs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<ToolExecutionLog[]>([]);
  const [servers, setServers] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [failureKindFilter, setFailureKindFilter] = useState<string>("all");
  const pageSize = 20;

  useEffect(() => {
    loadServers();
  }, []);

  useEffect(() => {
    loadLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, failureKindFilter]);

  const loadServers = async () => {
    try {
      const serversData = await serverService.getAllServers();
      const serverMap = new Map<string, string>();
      serversData.forEach((server: ServerConfig) => {
        serverMap.set(server.id!, server.name);
      });
      setServers(serverMap);
    } catch (err) {
      console.error("Failed to load servers:", err);
    }
  };

  const loadLogs = async (pageToLoad: number) => {
    try {
      setLoading(true);
      const isSuccess =
        statusFilter === "success"
          ? true
          : statusFilter === "error"
            ? false
            : undefined;
      const data: PaginatedResult<ToolExecutionLog> =
        await toolExecutionLogsService.getAllLogs(
          pageToLoad,
          pageSize,
          search || undefined,
          isSuccess,
          failureKindFilter === "all"
            ? undefined
            : (failureKindFilter as FailureKind),
        );
      setLogs(data.items);
      setTotal(data.total);
      setError("");
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("app.toolExecutionLogs.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string[]) => {
    setStatusFilter(value[0] || "all");
    setPage(1);
  };

  const handleFailureKindFilterChange = (value: string[]) => {
    setFailureKindFilter(value[0] || "all");
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDuration = (ms?: number) => {
    if (ms === undefined || ms === null) return "-";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const truncateToolName = (name: string) => {
    const endLength = 20;
    if (!name || name.length <= endLength) return name;
    return `***${name.slice(-endLength)}`;
  };

  const truncateEndpoint = (endpoint: string) => {
    const endLength = 20;
    if (!endpoint || endpoint.length <= endLength) return endpoint;
    return `***${endpoint.slice(-endLength)}`;
  };

  const getServerName = (serverId: string) => {
    return servers.get(serverId) || serverId;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Flex
      direction="column"
      w="full"
      maxW="100%"
      overflowX="hidden"
      gap={5}
      minW={0}
    >
      <Flex justify="space-between" align="center">
        <HStack>
          <MdList size={32} color={getThemeColor("icon").base} />
          <Heading
            size="2xl"
            fontWeight="bold"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.toolExecutionLogs.title")}
          </Heading>
        </HStack>
        <HStack gap={3}>
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.toolExecutionLogs")}
          />
          <RefreshButton onClick={() => loadLogs(page)} loading={loading} />
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text>{error}</Text>
        </InfoBox>
      )}

      {/* Search and Filter */}
      <Card.Root>
        <Card.Body>
          <HStack gap={3}>
            <Input
              placeholder={t("app.toolExecutionLogs.search")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              w="400px"
            />
            <Button onClick={handleSearch} colorPalette="blue">
              <MdSearch /> {t("app.common.search")}
            </Button>
            <Select
              value={[statusFilter]}
              onValueChange={(details) =>
                handleStatusFilterChange(details.value)
              }
              items={[
                { label: t("app.toolExecutionLogs.allStatuses"), value: "all" },
                {
                  label: t("app.toolExecutionLogs.successOnly"),
                  value: "success",
                },
                { label: t("app.toolExecutionLogs.errorOnly"), value: "error" },
              ]}
              w="200px"
            />
            <Select
              value={[failureKindFilter]}
              onValueChange={(details) =>
                handleFailureKindFilterChange(details.value)
              }
              items={[
                {
                  label: t("app.toolExecutionLogs.allFailureKinds"),
                  value: "all",
                },
                ...FAILURE_KINDS.map((kind) => ({
                  label: t(`app.toolExecutionLogs.failureKind.${kind}`),
                  value: kind,
                })),
              ]}
              w="220px"
            />
          </HStack>
        </Card.Body>
      </Card.Root>

      {/* Logs Table */}
      <Card.Root w="full" maxW="100%">
        <Card.Body
          p={0}
          overflowX="auto"
          overflowY="hidden"
          w="full"
          maxW="100%"
          minW={0}
        >
          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <Text color={{ base: "gray.600", _dark: "white" }}>
                {t("app.common.loading")}
              </Text>
            </Flex>
          ) : logs.length === 0 ? (
            <Flex justify="center" align="center" minH="200px">
              <Text color={{ base: "gray.600", _dark: "white" }}>
                {t("app.toolExecutionLogs.noLogs")}
              </Text>
            </Flex>
          ) : (
            <Table.Root variant="outline" size="sm" minW="900px" w="full">
              <Table.Header>
                <Table.Row bg={{ base: "gray.50", _dark: "gray.800" }}>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.status")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.serverName")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.toolName")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.endpoint")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.method")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.statusCode")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.requestSize")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.responseSize")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.duration")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.toolExecutionLogs.executedAt")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.common.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {logs.map((log) => (
                  <Table.Row
                    key={log.id}
                    cursor="pointer"
                    onClick={() =>
                      navigate(`/admin/tool-execution-logs/${log.id}`)
                    }
                  >
                    <Table.Cell>
                      <HStack>
                        {log.isSuccess ? (
                          <MdCheckCircle color="green" size={20} />
                        ) : (
                          <MdError color="red" size={20} />
                        )}
                        <Badge
                          colorPalette={log.isSuccess ? "green" : "red"}
                          variant="outline"
                        >
                          {log.httpStatusCode === 0
                            ? t("app.toolExecutionLogs.notDispatched")
                            : log.httpStatusCode}
                        </Badge>
                        {log.attempt !== undefined && log.attempt > 1 && (
                          <Badge
                            colorPalette="orange"
                            variant="subtle"
                            title={t("app.toolExecutionLogs.attemptHint")}
                          >
                            {t("app.toolExecutionLogs.attemptBadge", {
                              n: log.attempt,
                            })}
                          </Badge>
                        )}
                        {log.failureKind && log.failureKind !== "none" && (
                          <Badge colorPalette="gray" variant="subtle">
                            {t(
                              `app.toolExecutionLogs.failureKind.${log.failureKind}`,
                            )}
                          </Badge>
                        )}
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" fontWeight="medium">
                        {getServerName(log.serverId)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell maxW="260px">
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        lineClamp={1}
                        title={log.toolName}
                      >
                        {truncateToolName(log.toolName)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell maxW="300px">
                      <Text fontSize="sm" lineClamp={1} title={log.endpoint}>
                        {truncateEndpoint(log.endpoint)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette="blue" variant="subtle">
                        {log.httpMethod}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {log.httpStatusCode === 0 ? "—" : log.httpStatusCode}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {formatBytes(log.requestLength)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {formatBytes(log.responseLength)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" fontWeight="medium">
                        {formatDuration(log.durationMs)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{formatDate(log.executedAt)}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/tool-execution-logs/${log.id}`);
                        }}
                        size="sm"
                        variant="ghost"
                        colorPalette="blue"
                        title={t("app.toolExecutionLogs.viewDetails")}
                      >
                        <MdVisibility />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Card.Body>
      </Card.Root>

      {/* Pagination */}
      {total > 0 && (
        <HStack justify="space-between">
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.common.showing")} {(page - 1) * pageSize + 1} -{" "}
            {Math.min(page * pageSize, total)} {t("app.common.of")} {total}
          </Text>
          <HStack>
            <Button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              size="sm"
            >
              {t("app.common.previous")}
            </Button>
            <Text fontSize="sm">
              {t("app.common.page")} {page} {t("app.common.of")} {totalPages}
            </Text>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              size="sm"
            >
              {t("app.common.next")}
            </Button>
          </HStack>
        </HStack>
      )}
    </Flex>
  );
};

export default ToolExecutionLogs;
